import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import SignaturePad from 'signature_pad';
import { INK } from '../util/renderTypedSignature.mjs';

/**
 * A pad to sign on - mouse, finger and pen alike (Pointer Events, through
 * signature_pad, whose variable-width strokes read as ink rather than a
 * line tool).
 *
 * Drawn at the screen's pixel density so the exported image is sharp, and
 * re-fitted when its box changes size - a phone turned sideways keeps what was
 * already drawn. The background stays transparent: the server crops to the
 * ink and the report prints the mark on white.
 *
 * Ref: clear(), isEmpty(), toDataURL() -> PNG data URL or null when empty.
 */
const SignatureCanvas = forwardRef(({ height = 180, onChange = null, className = '', label = 'Signature pad' }, ref) => {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext('2d')) return undefined;

    const pad = new SignaturePad(canvas, {
      penColor: INK,
      minWidth: 0.9,
      maxWidth: 2.8,
      backgroundColor: 'rgba(0,0,0,0)',
    });
    padRef.current = pad;
    const notify = () => onChangeRef.current?.(!pad.isEmpty());
    pad.addEventListener('endStroke', notify);

    const fit = () => {
      if (!canvas.offsetWidth) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const strokes = pad.toData();
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      pad.clear();
      if (strokes.length) pad.fromData(strokes);
    };
    fit();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (observer) observer.observe(canvas);
    else window.addEventListener('resize', fit);

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', fit);
      pad.removeEventListener('endStroke', notify);
      pad.off();
      padRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      padRef.current?.clear();
      onChangeRef.current?.(false);
    },
    isEmpty: () => !padRef.current || padRef.current.isEmpty(),
    toDataURL: () => (padRef.current && !padRef.current.isEmpty()
      ? padRef.current.toDataURL('image/png')
      : null),
  }), []);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={label}
      // touch-action none: a finger on the pad draws instead of scrolling.
      style={{ height, touchAction: 'none' }}
      className={`block w-full cursor-crosshair ${className}`}
    />
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

SignatureCanvas.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.string,
};

export default SignatureCanvas;
